import { useState } from "react";
import { Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Animated CSS/SVG diagrams — replaces the old "Video coming soon" placeholder
 * with an actual visual explainer for each jargon term.
 *
 * Every animation is pure SVG + CSS keyframes (no external assets, offline-safe).
 */
const KEYFRAMES = `
@keyframes kh-price-drop { 0% { transform: translateY(0); } 50% { transform: translateY(48px); } 100% { transform: translateY(24px); } }
@keyframes kh-fill-shift { 0% { transform: translateX(0); opacity: 0.4; } 60% { transform: translateX(28px); opacity: 1; } 100% { transform: translateX(28px); opacity: 0.4; } }
@keyframes kh-bar-grow-a { 0%, 100% { height: 20px; } 50% { height: 70px; } }
@keyframes kh-bar-grow-b { 0%, 100% { height: 55px; } 50% { height: 30px; } }
@keyframes kh-bar-grow-c { 0%, 100% { height: 38px; } 50% { height: 84px; } }
@keyframes kh-pulse { 0%, 100% { opacity: 0.35; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }
@keyframes kh-copy-shift { 0% { transform: translateX(0); } 100% { transform: translateX(72px); } }
@keyframes kh-tariff-slide { 0% { width: 100%; } 100% { width: 68%; } }
@keyframes kh-cal-tick { 0%, 20% { fill: hsl(var(--muted)); } 60%, 100% { fill: hsl(var(--accent)); } }
@keyframes kh-refer-flow { 0% { stroke-dashoffset: 200; } 100% { stroke-dashoffset: 0; } }
@keyframes kh-consistency-jitter { 0%, 100% { transform: translateY(0); } 25% { transform: translateY(-6px); } 75% { transform: translateY(6px); } }
`;

/* Renders inside an <aspect-video> box. Uses currentColor primary + accent. */
const Animations = {
    /* Drawdown — falling candle from a peak line */
    "Drawdown": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <line x1="10" y1="30" x2="230" y2="30" stroke={colors.accent} strokeWidth="2" strokeDasharray="4 4" />
            <text x="12" y="24" fontSize="9" fill={colors.accent} fontWeight="700">PEAK EQUITY</text>
            <line x1="10" y1="100" x2="230" y2="100" stroke={colors.danger} strokeWidth="2" strokeDasharray="4 4" />
            <text x="12" y="115" fontSize="9" fill={colors.danger} fontWeight="700">DRAWDOWN LIMIT</text>
            <g style={{ animation: "kh-price-drop 2.6s ease-in-out infinite" }}>
                <rect x="110" y="24" width="20" height="12" fill={colors.primary} />
                <line x1="120" y1="16" x2="120" y2="42" stroke={colors.primary} strokeWidth="2" />
            </g>
            <circle cx="120" cy="102" r="6" fill={colors.danger} style={{ animation: "kh-pulse 1.4s infinite" }} />
        </svg>
    ),
    /* Slippage — bid vs fill mismatch */
    "Slippage": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <rect x="30" y="52" width="60" height="36" rx="4" fill={colors.muted} />
            <text x="60" y="42" fontSize="10" textAnchor="middle" fill={colors.foreground} fontWeight="700">EXPECTED</text>
            <text x="60" y="75" fontSize="14" textAnchor="middle" fontWeight="800" fill={colors.foreground}>17.85</text>
            <g style={{ animation: "kh-fill-shift 2.2s ease-in-out infinite" }}>
                <path d="M100 70 L138 70" stroke={colors.primary} strokeWidth="2" markerEnd="url(#kh-arrow)" />
                <circle cx="118" cy="70" r="4" fill={colors.primary} />
            </g>
            <rect x="150" y="52" width="60" height="36" rx="4" fill={colors.primaryLight} stroke={colors.primary} />
            <text x="180" y="42" fontSize="10" textAnchor="middle" fill={colors.primary} fontWeight="700">FILL @ +0.5%</text>
            <text x="180" y="75" fontSize="14" textAnchor="middle" fontWeight="800" fill={colors.primary}>17.94</text>
            <defs>
                <marker id="kh-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M0 0 L10 5 L0 10 z" fill={colors.primary} />
                </marker>
            </defs>
        </svg>
    ),
    /* Profit Factor — bar comparison */
    "Profit Factor": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <g transform="translate(80 110)">
                <rect x="-30" y="-70" width="24" height="70" fill={colors.success}
                      style={{ animation: "kh-bar-grow-a 3s ease-in-out infinite", transformOrigin: "bottom" }} />
                <text x="-18" y="14" fontSize="10" textAnchor="middle" fontWeight="700" fill={colors.success}>GROSS WIN</text>
            </g>
            <g transform="translate(160 110)">
                <rect x="-12" y="-40" width="24" height="40" fill={colors.danger}
                      style={{ animation: "kh-bar-grow-b 3s ease-in-out infinite", transformOrigin: "bottom" }} />
                <text x="0" y="14" fontSize="10" textAnchor="middle" fontWeight="700" fill={colors.danger}>GROSS LOSS</text>
            </g>
            <text x="120" y="30" fontSize="12" textAnchor="middle" fontWeight="800" fill={colors.foreground}>PF = Wins ÷ Losses</text>
            <text x="120" y="50" fontSize="16" textAnchor="middle" fontWeight="800" fill={colors.primary}>&gt; 1.5 = Pro</text>
        </svg>
    ),
    /* Sharpe / Consistency — line jittering less around a mean */
    "Sharpe Ratio": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <line x1="20" y1="70" x2="220" y2="70" stroke={colors.muted} strokeWidth="1" />
            <text x="24" y="64" fontSize="9" fill={colors.mutedText}>MEAN RETURN</text>
            <g style={{ animation: "kh-consistency-jitter 2s ease-in-out infinite" }}>
                <polyline points="30,74 55,68 80,72 105,66 130,74 155,70 180,68 205,72"
                          fill="none" stroke={colors.success} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
            </g>
            <text x="120" y="118" fontSize="11" textAnchor="middle" fontWeight="700" fill={colors.foreground}>
                Low σ = predictable edge
            </text>
        </svg>
    ),
    /* Copy-Trading Ratio — proportional replication */
    "Copy-Trading Ratio": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <circle cx="50" cy="70" r="22" fill={colors.primary} />
            <text x="50" y="74" fontSize="11" textAnchor="middle" fontWeight="800" fill="#fff">LEAD</text>
            <text x="50" y="108" fontSize="9" textAnchor="middle" fill={colors.mutedText}>KES 500k</text>
            <g style={{ animation: "kh-copy-shift 2.4s ease-in-out infinite", opacity: 0.9 }}>
                <path d="M75 70 L120 70" stroke={colors.accent} strokeWidth="2" strokeDasharray="4 3" />
            </g>
            <circle cx="180" cy="70" r="14" fill={colors.accent} />
            <text x="180" y="73" fontSize="9" textAnchor="middle" fontWeight="800" fill="#fff">FOL</text>
            <text x="180" y="98" fontSize="9" textAnchor="middle" fill={colors.mutedText}>KES 100k</text>
            <text x="180" y="112" fontSize="9" textAnchor="middle" fontWeight="700" fill={colors.accent}>× 0.2 SIZE</text>
        </svg>
    ),
    /* B2C Tariff illustration — retired Feb 2026 alongside the M-Pesa
       payout flow. Kept the export map slot empty so any stale
       `<InfoTooltip term="M-Pesa B2C Tariff" />` reference degrades to
       the default fallback tile instead of crashing. */
    /* Minimum Trading Days — calendar ticks */
    "Minimum Trading Days": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <text x="120" y="24" fontSize="11" textAnchor="middle" fontWeight="700" fill={colors.foreground}>MIN 5 UNIQUE DAYS · MONTHLY</text>
            {[0, 1, 2, 3, 4].map((i) => (
                <rect key={i} x={30 + i * 36} y={50} width="28" height="28" rx="4"
                      fill={colors.muted}
                      style={{ animation: `kh-cal-tick 2.6s ease-in-out infinite`, animationDelay: `${i * 0.3}s` }} />
            ))}
            {[0, 1, 2, 3, 4].map((i) => (
                <text key={`t${i}`} x={44 + i * 36} y={68} fontSize="10" textAnchor="middle" fontWeight="800" fill={colors.foreground}>
                    D{i + 1}
                </text>
            ))}
            <text x="120" y="112" fontSize="10" textAnchor="middle" fill={colors.mutedText}>
                Each day you place ≥ 1 trade counts once.
            </text>
        </svg>
    ),
    /* Referral Credit — flow arrow */
    "Referral Credit": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <circle cx="42" cy="70" r="22" fill={colors.primary} />
            <text x="42" y="74" fontSize="10" textAnchor="middle" fontWeight="800" fill="#fff">YOU</text>
            <circle cx="198" cy="70" r="22" fill={colors.accent} />
            <text x="198" y="74" fontSize="10" textAnchor="middle" fontWeight="800" fill="#fff">FRIEND</text>
            <path d="M68 70 L172 70" stroke={colors.foreground} strokeWidth="2" strokeDasharray="200"
                  style={{ animation: "kh-refer-flow 2.8s linear infinite" }} />
            <text x="120" y="60" fontSize="10" textAnchor="middle" fontWeight="700" fill={colors.foreground}>SHARE CODE</text>
            <text x="120" y="94" fontSize="10" textAnchor="middle" fontWeight="700" fill={colors.accent}>+7 DAYS FREE</text>
            <text x="120" y="118" fontSize="9" textAnchor="middle" fill={colors.mutedText}>Applied automatically on next billing cycle.</text>
        </svg>
    ),
    /* Fallback — generic ticker */
    "Max Drawdown": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <polyline points="10,50 40,42 70,58 100,44 130,72 160,88 190,100 220,110"
                      fill="none" stroke={colors.primary} strokeWidth="3" strokeLinejoin="round" />
            <line x1="10" y1="110" x2="220" y2="110" stroke={colors.danger} strokeDasharray="4 4" strokeWidth="2" />
            <text x="14" y="126" fontSize="9" fontWeight="700" fill={colors.danger}>−10% MAX DD · FROZEN</text>
        </svg>
    ),
    /* Avg Risk-to-Reward — two bars side-by-side showing target reward > risked amount */
    "Avg R:R": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <text x="120" y="24" fontSize="11" textAnchor="middle" fontWeight="800" fill={colors.foreground}>REWARD-TO-RISK RATIO</text>
            {/* Risk bar (small, danger) */}
            <g transform="translate(70 110)">
                <rect x="-16" y="-32" width="32" height="32" fill={colors.danger}
                      style={{ animation: "kh-bar-grow-b 3s ease-in-out infinite", transformOrigin: "bottom" }} />
                <text x="0" y="14" fontSize="10" textAnchor="middle" fontWeight="700" fill={colors.danger}>RISK · 1R</text>
            </g>
            {/* Reward bar (larger, success) */}
            <g transform="translate(170 110)">
                <rect x="-16" y="-72" width="32" height="72" fill={colors.success}
                      style={{ animation: "kh-bar-grow-a 3s ease-in-out infinite", transformOrigin: "bottom" }} />
                <text x="0" y="14" fontSize="10" textAnchor="middle" fontWeight="700" fill={colors.success}>REWARD · 2R</text>
            </g>
            {/* Arrow between */}
            <path d="M92 70 L148 70" stroke={colors.foreground} strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#rr-arrow)" />
            <defs>
                <marker id="rr-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M0 0 L10 5 L0 10 z" fill={colors.foreground} />
                </marker>
            </defs>
            <text x="120" y="60" fontSize="10" textAnchor="middle" fontWeight="700" fill={colors.primary}>R:R = 2.0 · Pro</text>
        </svg>
    ),
    /* Consistency Score — reuses the Sharpe-style jitter which is the perfect visual */
    "Consistency Score": ({ colors }) => (
        <svg viewBox="0 0 240 140" className="w-full h-full">
            <line x1="20" y1="70" x2="220" y2="70" stroke={colors.muted} strokeWidth="1" />
            <text x="24" y="64" fontSize="9" fill={colors.mutedText}>MEAN DAILY RETURN</text>
            <g style={{ animation: "kh-consistency-jitter 2s ease-in-out infinite" }}>
                <polyline points="30,74 55,68 80,72 105,66 130,74 155,70 180,68 205,72"
                          fill="none" stroke={colors.success} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
            </g>
            <text x="120" y="118" fontSize="11" textAnchor="middle" fontWeight="700" fill={colors.foreground}>
                Tight band around mean = consistent
            </text>
        </svg>
    ),
};

function useColors() {
    // Read from the resolved CSS variables so we automatically follow the theme
    if (typeof window === "undefined") {
        return { foreground: "#000", primary: "#922529", accent: "#006600", danger: "#922529", success: "#006600", muted: "#eee", mutedText: "#666", primaryLight: "#f5e5e5" };
    }
    const s = getComputedStyle(document.documentElement);
    const hsl = (v) => `hsl(${v})`;
    const hslA = (v, a) => `hsl(${v} / ${a})`;
    const primary = s.getPropertyValue("--primary").trim();
    return {
        foreground: hsl(s.getPropertyValue("--foreground").trim() || "0 0% 0%"),
        primary: hsl(primary || "358 60% 36%"),
        primaryLight: hslA(primary || "358 60% 36%", 0.14),
        accent: hsl(s.getPropertyValue("--accent").trim() || "120 100% 20%"),
        danger: hsl(s.getPropertyValue("--danger").trim() || "358 60% 36%"),
        success: hsl(s.getPropertyValue("--success").trim() || "120 100% 20%"),
        muted: hsl(s.getPropertyValue("--muted").trim() || "0 0% 92%"),
        mutedText: hsl(s.getPropertyValue("--muted-foreground").trim() || "0 0% 40%"),
    };
}

function TermAnimation({ term }) {
    const colors = useColors();
    const A = Animations[term] || Animations["Max Drawdown"];
    return (
        <div className="aspect-video w-full rounded-md border border-border overflow-hidden relative bg-secondary/40">
            <style>{KEYFRAMES}</style>
            <A colors={colors} />
        </div>
    );
}

/**
 * InfoTooltip — (i) icon that opens a modal with:
 *   1. A short, plain-language DEFINITION (top of modal, prominent).
 *   2. An animated visual explainer of the concept.
 *
 * Both surfaces reinforce the same idea for learners who prefer text-first
 * OR visual-first. The definition text comes from the parent via `children`.
 */
export default function InfoTooltip({ term, children, testId }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center justify-center align-middle text-muted-foreground hover:text-primary transition-colors"
                aria-label={`Learn about ${term}`}
                data-testid={testId || `info-${term.toLowerCase().replace(/\s+/g, "-")}`}
            >
                <Info className="w-3.5 h-3.5" />
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="tracking-tight">{term}</DialogTitle>
                        <DialogDescription className="sr-only">Definition and animated explainer for {term}</DialogDescription>
                    </DialogHeader>

                    {/* Plain-text definition — the text-first learner's answer.
                        Rendered PROMINENTLY above the animation so users who
                        just want the definition don't have to hunt below the
                        SVG. */}
                    {children && (
                        <div
                            className="rounded-md border-l-4 border-primary bg-primary/5 px-3 py-2.5"
                            data-testid={`info-definition-${term.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <div className="text-[10px] uppercase tracking-widest font-extrabold text-primary mb-1">
                                Definition
                            </div>
                            <p className="text-sm leading-relaxed text-foreground">{children}</p>
                        </div>
                    )}

                    {/* Visual explainer for the pattern-recognition learners */}
                    <div className="mt-1">
                        <div className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">
                            Visual explainer
                        </div>
                        <TermAnimation term={term} />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
